/**
 * Pure helpers for the Tabbio CLI browser hand-off page (`/cli/connect`).
 *
 * `tabbio login` starts an HTTP listener on `127.0.0.1:<port>` and opens
 * `/cli/connect?port=<port>&state=<state>&device=<hostname>` in the browser
 * (RFC 8252 loopback redirect). After the signed-in user approves, the page
 * mints a one-time grant code and sends the browser to
 * `http://127.0.0.1:<port>/callback?code=<code>&state=<state>`, where the CLI
 * swaps the code for its own session.
 *
 * Every decision about what the page is willing to redirect to lives here so
 * it can be unit tested without a renderer
 * (`src/__tests__/cli-connect.test.ts`). The rules are deliberately narrow:
 *
 * - the destination is ALWAYS `http://127.0.0.1:<port>/callback`. Never
 *   `localhost` (which can resolve to something else), never https (the
 *   listener has no certificate), never another host or path. The query is
 *   either `code=…&state=…` (approved) or `error=access_denied&state=…`
 *   (cancelled, RFC 6749 §4.1.2.1, so the terminal stops waiting at once);
 * - `port` must be a plain integer in 1024..65535 (no privileged ports);
 * - `state` must look like the CLI's random nonce, so nothing else can ride
 *   along in the query string;
 * - `device` is display-only and is sanitised before it is shown.
 */
import { fromBase64Url, toBase64Url } from './oauth-consent';

type QueryValue = string | string[] | undefined;

export const CLI_CONNECT_MIN_PORT = 1024;
export const CLI_CONNECT_MAX_PORT = 65535;
export const CLI_CONNECT_STATE_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
export const CLI_CONNECT_DEVICE_MAX_LENGTH = 64;

/** The only host the page will ever redirect to. */
export const CLI_CONNECT_LOOPBACK_HOST = '127.0.0.1';

/** The only path on that host the page will ever redirect to. */
export const CLI_CONNECT_CALLBACK_PATH = '/callback';

export type CliConnectParams = {
  port: number;
  state: string;
  /** Sanitised display label, or null when none was sent or nothing survived. */
  device: string | null;
};

export type CliConnectParseResult =
  | { ok: true; params: CliConnectParams }
  | { ok: false; reason: 'missing' | 'invalid-port' | 'invalid-state' };

/**
 * A repeated query parameter (`?port=1&port=2`) is ambiguous, so it is treated
 * as absent rather than guessing which copy the CLI meant.
 */
function single(value: QueryValue): string | null {
  if (Array.isArray(value)) return null;
  return typeof value === 'string' ? value : null;
}

export function parseCliConnectPort(raw: QueryValue): number | null {
  const value = single(raw);
  // Digits only: rejects `8080.5`, `0x1f90`, `+8080`, ` 8080`, `1e4`.
  if (!value || !/^\d{1,5}$/.test(value)) return null;
  const port = Number(value);
  if (!Number.isInteger(port)) return null;
  if (port < CLI_CONNECT_MIN_PORT || port > CLI_CONNECT_MAX_PORT) return null;
  return port;
}

export function isValidCliConnectState(value: unknown): value is string {
  return typeof value === 'string' && CLI_CONNECT_STATE_PATTERN.test(value);
}

// Characters that can make a label lie about itself: C0/C1 controls, zero-width
// joiners and spaces, the byte order mark, and the bidi embedding / override /
// isolate controls (a hostile label could otherwise render "moc.evil" as
// "evil.com" or push text outside its box).
const UNSAFE_LABEL_CHARACTERS =
  /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g;

/**
 * The device label is whatever the CLI read from `os.hostname()`, so it is
 * untrusted display text. React escapes it; this strips characters that could
 * spoof its appearance, collapses whitespace and caps the length (by code
 * point, so a surrogate pair is never cut in half).
 */
export function sanitizeCliDeviceLabel(raw: QueryValue): string | null {
  const value = single(raw);
  if (!value) return null;

  // Whitespace first, so a newline or tab becomes a space instead of gluing
  // two words together when the remaining control characters are stripped.
  const cleaned = value
    .replace(/\s+/g, ' ')
    .replace(UNSAFE_LABEL_CHARACTERS, '')
    .replace(/ {2,}/g, ' ')
    .trim();
  if (!cleaned) return null;

  const codePoints = Array.from(cleaned);
  if (codePoints.length <= CLI_CONNECT_DEVICE_MAX_LENGTH) return cleaned;
  return `${codePoints
    .slice(0, CLI_CONNECT_DEVICE_MAX_LENGTH - 1)
    .join('')
    .trimEnd()}…`;
}

/** Read and validate `useLocalSearchParams()` output for `/cli/connect`. */
export function parseCliConnectParams(
  params: Record<string, QueryValue>,
): CliConnectParseResult {
  const rawPort = params.port;
  const rawState = params.state;

  if (rawPort === undefined && rawState === undefined) {
    return { ok: false, reason: 'missing' };
  }

  const port = parseCliConnectPort(rawPort);
  if (port === null) return { ok: false, reason: 'invalid-port' };

  const state = single(rawState);
  if (!isValidCliConnectState(state)) {
    return { ok: false, reason: 'invalid-state' };
  }

  return {
    ok: true,
    params: { port, state, device: sanitizeCliDeviceLabel(params.device) },
  };
}

/**
 * `http://127.0.0.1:<port>/callback` is the destination shown to the user
 * before they approve. Throws on anything that is not a valid loopback port so
 * a bad value can never be rendered as if it were safe.
 */
export function getCliCallbackOrigin(port: number): string {
  if (
    !Number.isInteger(port) ||
    port < CLI_CONNECT_MIN_PORT ||
    port > CLI_CONNECT_MAX_PORT
  ) {
    throw new Error('Invalid CLI loopback port');
  }
  return `http://${CLI_CONNECT_LOOPBACK_HOST}:${port}${CLI_CONNECT_CALLBACK_PATH}`;
}

/**
 * The full redirect handed to `window.location.assign` after approval.
 * Built from validated parts only; the code and state are percent-encoded even
 * though both are base64url today, so a future server change cannot break out
 * of the query.
 */
export function buildCliCallbackUrl(
  port: number,
  code: string,
  state: string,
): string {
  if (!code) throw new Error('Missing grant code');
  if (!isValidCliConnectState(state)) throw new Error('Invalid CLI state');
  return `${getCliCallbackOrigin(port)}?code=${encodeURIComponent(
    code,
  )}&state=${encodeURIComponent(state)}`;
}

/**
 * The redirect for Cancel: tells the waiting CLI the user declined, so it exits
 * straight away instead of sitting on its listener until the 5-minute timeout.
 * Carries no secret.
 */
export function buildCliCancelUrl(port: number, state: string): string {
  if (!isValidCliConnectState(state)) throw new Error('Invalid CLI state');
  return `${getCliCallbackOrigin(port)}?error=access_denied&state=${encodeURIComponent(
    state,
  )}`;
}

/**
 * The page's own query, rebuilt from validated values. Percent encoding keeps
 * it pure ASCII, which is what lets the base64url post-auth encoding below be a
 * byte-for-byte transform.
 */
export function buildCliConnectQuery(params: CliConnectParams): string {
  const entries: [string, string][] = [
    ['port', String(params.port)],
    ['state', params.state],
  ];
  if (params.device) entries.push(['device', params.device]);
  return entries
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

/** `/cli/connect?port=…&state=…&device=…` */
export function buildCliConnectPath(params: CliConnectParams): `/cli/connect?${string}` {
  return `/cli/connect?${buildCliConnectQuery(params)}`;
}

// ---------------------------------------------------------------------------
// Post-auth target (`cli-connect:<base64url query>`)
// ---------------------------------------------------------------------------

const CLI_CONNECT_TARGET_PREFIX = 'cli-connect:';
const MAX_ENCODED_TARGET_LENGTH = 1024;
const ENCODED_TARGET_PATTERN = /^[A-Za-z0-9_-]+$/;

function parseQueryString(query: string): Record<string, QueryValue> {
  const out: Record<string, QueryValue> = {};
  for (const pair of query.split('&')) {
    if (!pair) continue;
    const index = pair.indexOf('=');
    const rawKey = index === -1 ? pair : pair.slice(0, index);
    const rawValue = index === -1 ? '' : pair.slice(index + 1);
    let key: string;
    let value: string;
    try {
      key = decodeURIComponent(rawKey);
      value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
    } catch {
      // A malformed escape makes the whole target untrustworthy.
      return {};
    }
    const existing = out[key];
    if (existing === undefined) out[key] = value;
    else out[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
  }
  return out;
}

/**
 * Park the hand-off as a post-auth target. Signing in remounts the navigator
 * and drops the URL, so the loopback port and state have to be stored rather
 * than carried (`lib/auth-handoff.ts` explains the race). base64url because
 * the target rides raw in `?next=`, where `&` and `%` would cut it in half.
 */
export function encodeCliConnectPostAuthTarget(
  params: CliConnectParams,
): `cli-connect:${string}` {
  return `cli-connect:${toBase64Url(buildCliConnectQuery(params))}`;
}

/**
 * The validated hand-off behind a `cli-connect:` target, or null when it is
 * unusable. Re-validates everything on the way out: a stored target is only as
 * trustworthy as whoever last wrote session storage.
 */
export function decodeCliConnectPostAuthTarget(
  target: string,
): CliConnectParams | null {
  if (!target.startsWith(CLI_CONNECT_TARGET_PREFIX)) return null;
  const encoded = target.slice(CLI_CONNECT_TARGET_PREFIX.length);
  if (
    encoded.length === 0 ||
    encoded.length > MAX_ENCODED_TARGET_LENGTH ||
    !ENCODED_TARGET_PATTERN.test(encoded)
  ) {
    return null;
  }
  const query = fromBase64Url(encoded);
  if (!query) return null;
  const parsed = parseCliConnectParams(parseQueryString(query));
  return parsed.ok ? parsed.params : null;
}

export type CliConnectOutcome = 'approved' | 'cancelled';

/**
 * Session-storage key recording how this exact hand-off ended, so a user who
 * presses Back (or reloads) sees the outcome instead of an Approve button that
 * would mint a second code for a listener that has already shut down.
 */
export function cliConnectOutcomeStorageKey(state: string): string {
  return `tabbio_cli_connect_outcome:${state}`;
}

export function parseCliConnectOutcome(value: string | null): CliConnectOutcome | null {
  return value === 'approved' || value === 'cancelled' ? value : null;
}
